import api from './api';
import {
  AnalyticsData,
  GlobalInsectRecordsResponse,
  HotspotsResponse,
  LiveNewsResponse,
  PredictionResponse,
  RecommendationFeedbackPayload,
  RecommendationFeedbackResponse,
  RegionalLiveRiskResponse,
} from '../types';

export interface PredictPayload {
  region: string;
  species: string;
  crop?: string;
  latitude: number;
  longitude: number;
}

export interface BoundingBox {
  minLat: number;
  minLon: number;
  maxLat: number;
  maxLon: number;
}

const DEFAULT_BBOX: BoundingBox = {
  minLat: 8,
  minLon: 68,
  maxLat: 37,
  maxLon: 97,
};

export const dashboardService = {
  async getAnalytics(): Promise<AnalyticsData> {
    const response = await api.get<AnalyticsData>('/analytics');
    return response.data;
  },

  async predict(payload: PredictPayload): Promise<PredictionResponse> {
    const response = await api.post<PredictionResponse>('/predict', payload);
    return response.data;
  },

  async submitRecommendationFeedback(
    predictionId: string,
    payload: RecommendationFeedbackPayload,
  ): Promise<RecommendationFeedbackResponse> {
    const response = await api.post<RecommendationFeedbackResponse>(
      `/predict/${predictionId}/recommendations/feedback`,
      payload,
    );
    return response.data;
  },

  async getHotspots(bbox: BoundingBox = DEFAULT_BBOX): Promise<HotspotsResponse> {
    const response = await api.get<HotspotsResponse>('/analytics/hotspots', {
      params: {
        min_lat: bbox.minLat,
        min_lon: bbox.minLon,
        max_lat: bbox.maxLat,
        max_lon: bbox.maxLon,
      },
    });
    return response.data;
  },

  async getGlobalInsectRecords(startYear?: number, endYear?: number, limit: number = 350): Promise<GlobalInsectRecordsResponse> {
    const response = await api.get<GlobalInsectRecordsResponse>('/analytics/global-records', {
      timeout: 60000,
      params: {
        start_year: startYear,
        end_year: endYear,
        limit,
      },
    });
    return response.data;
  },

  async getGlobalInsectRecordsForecast(
    startYear?: number,
    endYear?: number,
    limit: number = 350,
    forecastYears: number = 5,
  ): Promise<GlobalInsectRecordsResponse> {
    const response = await api.get<GlobalInsectRecordsResponse>('/analytics/global-records/forecast', {
      timeout: 90000,
      params: {
        start_year: startYear,
        end_year: endYear,
        limit,
        forecast_years: forecastYears,
      },
    });
    return response.data;
  },

  async getRegionalLiveRisk(startYear?: number, endYear?: number, species?: string, limit: number = 700): Promise<RegionalLiveRiskResponse> {
    const response = await api.get<RegionalLiveRiskResponse>('/analytics/regional-live-risk', {
      timeout: 90000,
      params: {
        start_year: startYear,
        end_year: endYear,
        species,
        limit,
      },
    });
    return response.data;
  },

  async getLiveNews(limit: number = 12): Promise<LiveNewsResponse> {
    const response = await api.get<LiveNewsResponse>('/analytics/live-news', {
      timeout: 20000,
      params: { limit },
    });
    return response.data;
  },
};
